import { useState } from 'react';
import { get, size, first, set } from 'global/lib/lodash';
import { wait } from 'global/utils';
import sendData from 'redux/actions/sendData';
import { UploadFiles } from 'redux/actions/imageUploader';

let queue = [];

export const ScorecardScoreIncludes =
  '?include=processedScorecardScoreValue,processedScorecardScoreValue.parent,processedScorecardScoreValue.parent.children,processedScorecardScoreValue.parent.parent,processedScorecardScoreValue.parent.parent.parent,processedScorecardScoreValue.subScores,processedScorecardScoreValue.subScores.images,processedScorecardScoreValue.subScores.realm,processedScorecardScoreValue.subScores.tradeTypes';

export const useQueue = (props) => {
  const { onUpdate, setAlerts } = props;
  const [_queue, setQueue] = useState(queue);

  const addToQueue = (d, opts = {}) => {
    let found = false;
    const qu = queue.map((q) => {
      if (q.id === d.id && !q.sending) {
        q = d;
        found = true;
      }
      return q;
    });
    queue = found ? qu : [...qu, { ...d, tries: 0 }];
    setQueue(queue);
    return processQueue(opts);
  };

  const removeFromQueue = (item) => {
    const index = queue.findIndex((q) => q.id === item.id);
    queue.splice(index, 1);
    setQueue([...queue]);
  };

  const retryItem = async (qitem) => {
    await wait(500);
    if (qitem.tries <= 5) {
      queue = queue.map((q) => {
        if (q.id === qitem.id) {
          q.sending = false;
          q.tries = (q.tries || 0) + 1;
        }
        return q;
      });
      setQueue(queue);
    } else {
      removeFromQueue(qitem);
    }
  };

  const processQueue = async (_opts = {}) => {
    const qitem = first(queue);
    try {
      const currentlySending = queue.find((q) => q.sending);
      if (!currentlySending && size(queue) > 0) {
        set(queue, '0.sending', true);

        const data = qitem.build();
        const scorecardScoreId = get(data, 'data.id');
        if (!data) return;

        const isArchived = get(data, 'data.attributes.archived');

        const opts = {
          maxTries: 0,
          normalize: { build: ['scorecardScores'] },
          ..._opts,
        };

        let resp;
        //grab generated id, so we can assign back after the response is received
        const ids = get(data, 'ids');
        delete data.ids;

        const images = get(data, 'data.attributes.images');
        if (size(images)) {
          await UploadFiles(data);
        }

        await Promise.all(
          (data.deletedMedia || []).map(async (dm) => {
            if (dm.id) {
              await sendData({
                url: `/media/${dm.id}`,
                method: 'delete',
              });
            }
          })
        );
        delete data.deletedMedia;

        if (isArchived) {
          if (!scorecardScoreId) {
            return retryItem(qitem);
          }

          resp = await sendData(
            {
              url: `/scorecard-scores/${scorecardScoreId}${ScorecardScoreIncludes}`,
              method: 'patch',
              data,
            },
            opts
          );
        } else {
          delete data.data.id;
          resp = await sendData(
            {
              url: `/scorecard-scores${ScorecardScoreIncludes}`,
              method: 'post',
              data,
            },
            opts
          );
        }

        //reset the original _ids so that we know what to update
        const psv = get(resp, '0.processedScorecardScoreValue');
        set(psv, ['_id'], get(ids, 'checkpoint'));
        set(psv, ['parent', '_id'], get(ids, 'checklist'));
        set(psv, ['parent', 'parent', '_id'], get(props, 'system._id'));
        set(psv, ['parent', 'parent', 'parent', '_id'], get(props, 'scorecard._id'));

        const update = opts.onUpdate || onUpdate;

        if (!psv) {
          console._warn('FAILED TO UPDATE');
          await update(null, true, resp);
          removeFromQueue(qitem);
        } else {
          await update(resp, false, resp);
          removeFromQueue(qitem);
          await wait();
        }
        processQueue();
      }
    } catch (err) {
      qitem.sending = false;
      qitem.tries += 1;
      if (qitem.tries >= 5) {
        removeFromQueue(qitem);
        setAlerts && setAlerts({
          message: `Error: ${err.message}`,
          severity: 'error',
        });
      } else {
        await wait(1000);
        processQueue();
      }
    }
  };

  const clearQueue = () => {
    queue = [];
    setQueue(queue);
  };

  return {
    queue,
    clearQueue,
    addToQueue,
  };
};
